// Topics & Subtopics — the shape both widgets read.
//
// Social Listening has no topic data on the backend yet (only inbox-analysis
// and review-analysis expose topics), so these are fed from a fixture for now.
// The shape is written the way an API would return it — counts rather than
// pre-computed widths — so wiring a real endpoint later is a swap of the data
// source, not a rewrite of the components.

// Mentions split by sentiment. Stored as counts, never as percentages: the
// widgets derive every width and label from these so a rounding choice is made
// in one place.
export interface TopicSentiment {
  negative: number
  neutral: number
  positive: number
}

export interface TopicEntry {
  // Period-over-period change, already a percentage. Positive is an increase.
  // null when there is no previous period to compare against — a new topic has
  // no delta, which is not the same as a delta of zero.
  delta: number | null
  id: string
  mentions: number
  name: string
  // Review Analysis only: a review topic is inherently praise or a pain point.
  polarity?: 'POS' | 'NEG' | null
  sentiment: TopicSentiment
  // Share of ALL mentions in the campaign, not just the ones shown. Supplied by
  // the source rather than derived, because the widgets only ever hold the top
  // few and cannot see the denominator.
  share: number
}

export interface TopicsOverviewData {
  // The one-line AI summary under the cards. Optional: no summary means the
  // row is not rendered at all rather than left as an empty band.
  insight?: string
  topSubtopics: TopicEntry[]
  topTopics: TopicEntry[]
  totalSubtopics: number
  totalTopics: number
}

// How many rows each "Top …" card shows. The card is sized for three in the
// design; more are dropped rather than scrolled.
export const TOP_ENTRY_LIMIT = 3

/**
 * How much of the bar track a topic fills.
 *
 * Relative to the largest entry on show, so the leading topic fills the track
 * and the rest are read against it. Returns 0 rather than NaN when nothing has
 * any mentions, which is what an empty campaign looks like.
 */
export const barFillRatio = (mentions: number, max: number): number => {
  if (!Number.isFinite(mentions) || !Number.isFinite(max) || max <= 0) return 0
  return Math.max(0, Math.min(1, mentions / max))
}

/**
 * The three sentiment segments as fractions of the filled bar.
 *
 * Guards the empty case: a topic with mentions but no sentiment breakdown (or
 * a breakdown of all zeroes) would otherwise divide by zero and render NaN
 * widths, which collapses the bar silently.
 */
export const sentimentFractions = (
  sentiment: TopicSentiment
): TopicSentiment => {
  const positive = Math.max(0, sentiment?.positive ?? 0)
  const neutral = Math.max(0, sentiment?.neutral ?? 0)
  const negative = Math.max(0, sentiment?.negative ?? 0)
  const total = positive + neutral + negative
  if (total <= 0) return { negative: 0, neutral: 0, positive: 0 }
  return {
    negative: negative / total,
    neutral: neutral / total,
    positive: positive / total,
  }
}

// The detail page's table carries more per topic than the overview card does.
export interface TopicTableEntry extends TopicEntry {
  // Recently discovered — drawn as a NEW pill beside the name.
  isNew?: boolean
  topSource: string
  // Mention counts over the period, oldest first, for the sparkline. Fewer
  // than two points cannot draw a line and render as empty.
  trend: number[]
}

export interface EmergingTopic {
  delta: number | null
  id: string
  mentions: number
  name: string
  share: number
}

export interface TopicsTableData {
  emerging: EmergingTopic[]
  topics: TopicTableEntry[]
}

// ---------------------------------------------------------------------------
// Topic detail modal
// ---------------------------------------------------------------------------

// A single post's sentiment, and the Related Mentions tab that narrows to it.
export type MentionSentiment = 'POS' | 'NEU' | 'NEG'

export interface PlatformShare {
  // Mentions of the topic from this platform in the window.
  mentions?: number
  platform: string
  // The platform's mentions split by sentiment: the bar's coloured segments.
  sentiment?: TopicSentiment
  share: number
}

export interface SubtopicRow {
  delta: number | null
  id: string
  mentions: number
  name: string
  sentiment: TopicSentiment
  // Share of the parent topic, not of the campaign.
  shareOfTopic: number
}

export interface RelatedMention {
  domain: string
  id: string
  language: string
  platform: string
  // Absolute timestamp; the label is formatted client-side so it stays correct
  // without a refetch.
  publishedAt?: string | null
  // Review Analysis only: the star rating and who wrote it.
  rating?: number | null
  reviewerName?: string | null
  // The post's own sentiment; null when it has none yet.
  sentiment?: MentionSentiment | null
  // Which subtopic this post was tagged with, when a classifier has run. Null
  // until then, which is why selecting a subtopic can legitimately show
  // nothing.
  subtopic?: string | null
  // The original wording. `translated` is the English rendering when one
  // exists and differs, for a See-translation toggle; null otherwise.
  text: string
  translated?: string | null
  url?: string
  // Review Analysis only: the location the review is about.
  venueName?: string | null
}

export interface MentionsPoint {
  label: string
  value: number
}

export type MentionsRange = '7-Day' | '14-Day' | '30-Day'

export interface TopicDetail {
  delta: number | null
  mentions: number
  // The topic, or the subtopic when the read was narrowed to one.
  name: string
  platforms: PlatformShare[]
  // Review Analysis only.
  polarity?: 'POS' | 'NEG' | null
  relatedMentions: RelatedMention[]
  sentiment: TopicSentiment
  // Share of the parent topic; null for a whole topic.
  shareOfTopic?: number | null
  // Share of all campaign mentions.
  shareOfTotal: number
  // The subtopic in view (null for a whole topic) and its parent topic — the
  // modal's "Topic › Subtopic" breadcrumb.
  subtopic?: string | null
  subtopics: SubtopicRow[]
  // One authored sentence about the topic, shown under its name. Review
  // Analysis writes it from the catalog; Social Listening has none yet.
  summary?: string | null
  topic?: string
  // One series per range so the toggle does not need a request per click.
  trend: Record<MentionsRange, MentionsPoint[]>
}

// Which sentiment leads, for the donut's headline. Ties fall to the larger of
// the two rather than to whichever happens to be first in the object.
export const dominantSentiment = (
  sentiment: TopicSentiment
): 'positive' | 'neutral' | 'negative' => {
  const { negative, neutral, positive } = sentiment
  if (positive >= neutral && positive >= negative) return 'positive'
  if (negative >= neutral) return 'negative'
  return 'neutral'
}

export const DOMINANT_LABEL: Record<string, string> = {
  negative: 'Mostly Negative',
  neutral: 'Mostly Neutral',
  positive: 'Mostly Positive',
}

// Axis ticks run 0% to the largest subtopic share, rounded up to a clean 10.
// Every bar is drawn against that same scale, so the leader fills the track.
// Never below 10, so a topic whose only subtopic holds 1% still gets an axis.
export const axisMax = (rows: { shareOfTopic: number }[]): number => {
  const largest = (rows ?? []).reduce(
    (acc, row) => Math.max(acc, row?.shareOfTopic ?? 0),
    0
  )
  return Math.max(10, Math.ceil(largest / 10) * 10)
}

export const axisTicks = (max: number): number[] =>
  Array.from({ length: Math.floor(max / 10) + 1 }, (_, index) => index * 10)
